import { useMutation, useQuery } from '@apollo/client'
import { useGlobal, useTranslate } from 'ur-react-hooks'
import { PageHeader } from 'components'
import { PROJECT_NAME_QUERY } from 'modules/projects/queries'
import { ProjectNameQuery } from 'modules/projects/types.graphql'
import React, { useLayoutEffect, useMemo } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { IdVariable } from 'types/graphql'
import { useBreadcrumbOverrides, useOnErrorAuto, useToast } from 'util/hooks'
import { OfferForm } from './components/OfferForm'
import { CREATE_OFFER_MUTATION } from './mutations'
import {
  CreateOfferMutation,
  CreateOfferMutationVariables,
} from './types.graphql'
import { isCreateOfferMutationVariables } from './util'

const Wrapper = styled.div`
  ${props => props.theme.layout.defaultWrapper};

  ${props => props.theme.media.mobile} {
    padding-left: 0;
    padding-right: 0;
  }
`

export const CreateOffer: React.FC = () => {
  const translations = useTranslate({
    createOffer: 'offers.create',
    createSuccess: 'offers.toasts.create-offer-success',
  })

  const { projectId } = useParams<{ projectId: string }>()
  const onErrorAuto = useOnErrorAuto()
  const history = useHistory()
  const addToast = useToast()
  const [, setHeaderIcon] = useGlobal('header.icon')

  useLayoutEffect(() => setHeaderIcon(null), [setHeaderIcon])

  const { data, loading } = useQuery<ProjectNameQuery, IdVariable>(
    PROJECT_NAME_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !projectId,
      variables: {
        id: projectId,
      },
      onError: onErrorAuto(),
    }
  )

  useBreadcrumbOverrides({
    [projectId]: data?.project.name ?? projectId,
    create: translations.createOffer,
  })

  const defaultHourlyRate = useMemo(() => data?.project.hourlyRate, [data])

  const [createOffer, { loading: createLoading }] = useMutation<
    CreateOfferMutation,
    CreateOfferMutationVariables
  >(CREATE_OFFER_MUTATION, {
    onCompleted(data) {
      addToast('success', translations.createSuccess)
      history.push({
        pathname: `/projects/${projectId}/offers`,
        state: { openModal: data.createOffer.offer.id },
      })
    },
    onError: onErrorAuto(),
  })

  const isLoading = useMemo(
    () => loading || createLoading,
    [createLoading, loading]
  )

  return (
    <Wrapper>
      {!isMobileOnly && (
        <PageHeader title={translations.createOffer} loading={isLoading} />
      )}

      <OfferForm
        projectId={projectId}
        defaultHourlyRate={defaultHourlyRate ?? 0}
        loading={loading}
        onSubmit={variables =>
          isCreateOfferMutationVariables(variables) &&
          createOffer({ variables })
        }
      />
    </Wrapper>
  )
}
